import {auth, db} from "../firebase";
import {
    collection, doc, getDocs, getDoc, addDoc, updateDoc, query, where, orderBy, limit, startAfter,
    setDoc, serverTimestamp
} from "firebase/firestore";
import {
    getPayoutCandidateIssue,
    normalizeIban,
    resolveRecipientName,
    sortPayoutCandidatesNewestFirst,
} from "../lib/payoutExport";
import {createEmployee as createEmployeeRecord} from "./onboardingService";


// projectId is either tiply-app-test or tiply-app. This is to determine which database connection to use.
const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;


const pick = (obj, allowedKeys) =>
    Object.fromEntries(
        Object.entries(obj || {}).filter(([k, v]) => allowedKeys.includes(k) && v !== undefined)
    );

/**
 * Get a week's shifts doc or null.
 */
export async function getShiftsWeek(businessID, weekKey) {
    const ref = doc(db, "businesses", businessID, "shifts", weekKey);
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
}

/**
 * Initialize a week's shifts doc if missing; returns the created doc data.
 * Expects: business object (for timezone/cutoff/period), businessID, weekKey, dayKeys array.
 */
export async function initShiftsWeekIfMissing({ business, businessID, weekKey, dayKeys }) {
    const ref = doc(db, "businesses", businessID, "shifts", weekKey);
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data();

    const initial = {
        businessID,
        weekKey,
        timezone: business?.config?.period?.timezone || "Europe/Tallinn",
        cutoffHourLocal: business?.config?.period?.cutoffHourLocal ?? 5,
        periodType: business?.config?.period?.type || "DAILY",
        status: "DRAFT", // DRAFT | PUBLISHED
        version: 1,
        source: "MANUAL",
        notes: "",
        days: Object.fromEntries((dayKeys || []).map(k => [k, []])),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: null,
        publishedBy: null,
    };
    await setDoc(ref, initial, { merge: false });
    return initial;
}

/**
 * Save shifts as draft (merge). Pass the full in-memory 'shifts' object.
 * Does not change status if already PUBLISHED.
 */
export async function saveShiftsDraft(businessID, weekKey, shifts) {
    const ref = doc(db, "businesses", businessID, "shifts", weekKey);
    // Prevent accidentally downgrading published weeks
    const existing = await getDoc(ref);
    const isPublished = existing.exists() && existing.data().status === "PUBLISHED";
    const payload = {
        ...shifts,
        updatedAt: serverTimestamp(),
    };
    if (!isPublished) {
        payload.status = "DRAFT";
    }
    await setDoc(ref, payload, { merge: true });
}

/**
 * Publish (lock) a week. Sets status = PUBLISHED and stamps audit fields.
 */
export async function publishShiftsWeek(businessID, weekKey, userId) {
    const ref = doc(db, "businesses", businessID, "shifts", weekKey);
    await updateDoc(ref, {
        status: "PUBLISHED",
        publishedAt: serverTimestamp(),
        publishedBy: userId || "system",
        updatedAt: serverTimestamp(),
    });
}



// BUSINESS CONFIG PAGE STUFF

export async function getBusinessConfig(businessID) {
    const ref = doc(db, "businesses", businessID);
    const snap = await getDoc(ref);
    const data = snap.exists() ? snap.data() : null;
    return data?.config || null;
}

// export async function updateBusinessConfig(businessID, config) {
//     console.log("businessID updateBusinessConfig: " + businessID)
//     const ref = doc(db, "businesses", businessID);
//     const snap = await getDoc(ref);
//     const prev = snap.exists() ? snap.data() : {};
//     await setDoc(ref, { ...prev, config }, { merge: true });
//
//     /* TODO update publicBusinesses with the necessary teamEnabled and personalEnabled flags
//         this way the TipPage knows how to act if tipID == 1 (the reserved tipID for Team page) and if its something else)
//     */
// }

export async function updateBusinessConfig(businessID, config) {
    const ref = doc(db, "businesses", businessID);
    await updateDoc(ref, {config});
}

export async function listEmployeesByBusiness(businessID) {
    const q = query(collection(db, "employees"), where("businessID", "==", businessID));
    const res = await getDocs(q);
    return res.docs.map((d) => ({id: d.id, ...d.data()}));
}


// END OF BUSINESS CONFIG PAGE STUFF


export const submitFeedbackOnly = async (
    businessID,
    employeeID,
    rating,
    feedback,
    businessName,
    employeeName,
    googlePlaceID,
    employeeImgUrl
) => {
    try {
        const response = await fetch(
            `https://us-central1-${projectId}.cloudfunctions.net/submitFeedbackOnly`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    businessID,
                    employeeID,
                    rating,
                    feedback,
                    businessName,
                    employeeName,
                    googlePlaceID,
                    employeeImgUrl
                })
            }
        );

        const data = await response.json();

        if (!response.ok) throw new Error(data.error || "Failed to submit feedback");

        return data.id; // return tip doc ID for redirect
    } catch (error) {
        console.error("❌ Failed to submit feedback:", error);
        return null;
    }
};


// export const updateBusinessData = async (businessID, updatedData) => {
//     const ref = doc(db, "businesses", businessID);
//     await updateDoc(ref, updatedData);
// };

/**
 * NB!!! DO NOT USE THIS FOR SENSITIVE INFO UPDATES!!!
 * @param businessID
 * @param updatedData
 * @returns {Promise<void>}
 */
// export const updateBusinessData = async (businessID, updatedData) => {
//     const businessRef = doc(db, "businesses", businessID);
//     const publicRef = doc(db, "publicBusinesses", businessID);
//
//     await Promise.all([
//         updateDoc(businessRef, updatedData),
//         updateDoc(publicRef, updatedData),
//     ]);
// };

export const updateBusinessData = async (businessID, updatedData) => {
    const businessRef = doc(db, "businesses", businessID);
    const OWNER_EDITABLE_KEYS = [
        "address",
        "config",
        "countryCode",
        "defaultTipAmount",
        "displayName",
        "googlePlaceID",
        "legalName",
        "repFirstName",
        "repLastName",
        "repPhone",
        "teamImgUrl",
        "tipAmountConfig",
    ];
    const privatePayload = pick(updatedData, OWNER_EDITABLE_KEYS);
    if (!Object.keys(privatePayload).length) return;
    await updateDoc(businessRef, {...privatePayload, updatedAt: serverTimestamp()});
};

/**
 * Fetches an employee by tipID and business name.
 */
export const getEmployeeByTipID = async (businessID, tipID) => {
    try {
        const employeesRef = collection(db, "businesses", businessID, "employees");
        const q = query(employeesRef, where("businessID", "==", businessID), where("tipID", "==", tipID));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            console.log(`❌ No employee found for tipID ${tipID} in business ${businessID}`);
            return null;
        }

        return {id: snapshot.docs[0].id, ...snapshot.docs[0].data()};
    } catch (error) {
        console.error("❌ Error fetching employee by tipID:", error);
        return null;
    }
};


// export const updateEmployeeData = async (businessID, employeeID, updatedData) => {
//     try {
//         // 1️⃣ Update private employee data
//         const employeeRef = doc(db, "businesses", businessID, "employees", employeeID);
//         await updateDoc(employeeRef, updatedData);
//         console.log("✅ Employee data updated in private collection");
//
//         // 2️⃣ Update public employee data
//         const publicEmployeeRef = doc(db, "publicBusinesses", businessID, "publicEmployees", employeeID);
//         await updateDoc(publicEmployeeRef, updatedData);
//         console.log("✅ Employee data updated in public collection");
//     } catch (error) {
//         console.error("❌ Error updating employee data:", error);
//     }
// };


export const updateEmployeeData = async (businessID, employeeID, updatedData) => {
    const employeeRef = doc(db, "businesses", businessID, "employees", employeeID);
    const OWNER_EDITABLE_KEYS = [
        "displayName",
        "email",
        "firstName",
        "iban",
        "imgUrl",
        "lastName",
        "name",
        "role",
    ];
    const privatePayload = pick(updatedData, OWNER_EDITABLE_KEYS);
    if (!Object.keys(privatePayload).length) return;
    await updateDoc(employeeRef, {...privatePayload, updatedAt: serverTimestamp()});
};


export const addEmployee = async (businessID, employeeData, tipPool, tipPoolManagerID) => {
    const displayName = String(
        employeeData?.displayName
        || `${employeeData?.firstName || ""} ${employeeData?.lastName || ""}`
    ).trim();
    const result = await createEmployeeRecord(businessID, displayName);
    const details = pick(employeeData, ["displayName", "email", "firstName", "iban", "imgUrl", "lastName", "name", "role"]);
    if (Object.values(details).some((value) => String(value || "").trim())) {
        await updateEmployeeData(businessID, result.employeeID, details);
    }
    return result;
};


/**
 * Fetches all businesses from Firestore.
 */
export const getAllBusinesses = async () => {
    try {
        const businessesRef = collection(db, "businesses");
        const snapshot = await getDocs(businessesRef);

        let businesses = [];
        snapshot.forEach((doc) => {
            businesses.push({id: doc.id, ...doc.data()});
        });

        return businesses;
    } catch (error) {
        console.error("❌ Error fetching businesses:", error);
        return [];
    }
};


/**
 * Adds a new business to Firestore.
 */
export const addBusiness = async (businessData) => {
    try {
        await addDoc(collection(db, "businesses"), {
            ...businessData,
            createdAt: new Date()
        });
        console.log("✅ Business added successfully.");
    } catch (error) {
        console.error("❌ Error adding business:", error);
    }
};


export const createPayout = async (payoutData) => {
    try {
        // 1️⃣ Create a new payout document in Firestore
        const payoutRef = await addDoc(collection(db, "payouts"), payoutData);
        const payoutID = payoutRef.id;

        // 2️⃣ Update each tip included in the payout
        const updatePromises = payoutData.tipIDs.map(async (tipID) => {
            const tipRef = doc(db, "tips", tipID);
            return updateDoc(tipRef, {
                payoutStatus: "PAID_OUT",
                payoutID: payoutID,
            });
        });

        await Promise.all(updatePromises); // Ensure all updates are completed

        console.log(`✅ Payout created (ID: ${payoutID}) & tips updated successfully.`);
        return payoutID;
    } catch (error) {
        console.error("❌ Error creating payout:", error);
        throw error;
    }
};

/**
 * Fetch all pending payouts (payoutStatus = "PENDING").
 */
export const getPendingPayouts = async () => {
    const tipsRef = collection(db, "tips");
    const pendingQuery = query(tipsRef, where("payoutStatus", "==", "PENDING"));

    const querySnapshot = await getDocs(pendingQuery);
    const tips = querySnapshot.docs.map((tipDoc) => ({...tipDoc.data(), id: tipDoc.id}));
    const employeeRefs = new Map();

    tips.forEach((tip) => {
        if (!tip.businessID || !tip.employeeID) return;
        const key = `${tip.businessID}/${tip.employeeID}`;
        if (!employeeRefs.has(key)) {
            employeeRefs.set(key, doc(db, "businesses", tip.businessID, "employees", tip.employeeID));
        }
    });

    const employeeEntries = Array.from(employeeRefs.entries());
    const employeeSnapshots = await Promise.all(employeeEntries.map(([, employeeRef]) => getDoc(employeeRef)));
    const employees = new Map(employeeEntries.map(([key], index) => [
        key,
        employeeSnapshots[index].exists() ? employeeSnapshots[index].data() : null,
    ]));

    const candidates = tips.map((tip) => {
        const employee = employees.get(`${tip.businessID}/${tip.employeeID}`) || {};
        const recipientName = resolveRecipientName(employee);
        const iban = normalizeIban(employee.iban);
        const validationError = getPayoutCandidateIssue({
            recipientName,
            iban,
            amount: tip.amount,
        });

        return {
            ...tip,
            recipientName,
            iban,
            validationError,
            eligible: !validationError,
        };
    });

    return sortPayoutCandidatesNewestFirst(candidates);
};

const callAuthenticatedFunction = async (functionName, body) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("You must be signed in as an administrator.");

    const idToken = await currentUser.getIdToken();
    const response = await fetch(
        `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`,
        {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${idToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(body),
        }
    );
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        const error = new Error(data.error || "The payout export request failed.");
        error.code = data.code;
        throw error;
    }
    return data;
};

export const createLhvPayoutBatch = async ({requestId, tipIDs}) => (
    callAuthenticatedFunction("createLhvPayoutBatch", {requestId, tipIDs})
);

export const downloadLhvPayoutBatch = async (batchID) => (
    callAuthenticatedFunction("downloadLhvPayoutBatch", {batchID})
);

export const getPayoutExportBatches = async ({pageSize = 20} = {}) => {
    const safePageSize = Number.isInteger(pageSize) && pageSize > 0 ? Math.min(pageSize, 50) : 20;
    const batchesQuery = query(
        collection(db, "payoutExportBatches"),
        orderBy("exportedAt", "desc"),
        limit(safePageSize)
    );
    const querySnapshot = await getDocs(batchesQuery);
    return querySnapshot.docs.map((batchDoc) => ({id: batchDoc.id, ...batchDoc.data()}));
};

/**
 * Fetch pending payouts for a specific business (payoutStatus = "PENDING").
 * @param {string} businessID - The business ID to filter payouts by.
 */
export const getPendingPayoutsByBusinessID = async (businessID) => {
    const tipsRef = collection(db, "tips");
    const pendingQuery = query(
        tipsRef,
        where("payoutStatus", "==", "PENDING"),
        where("businessID", "==", businessID)
    );

    const querySnapshot = await getDocs(pendingQuery);
    let pendingPayouts = {};

    querySnapshot.forEach((doc) => {
        const tip = {id: doc.id, ...doc.data()};
        const {employeeID, amount} = tip;

        if (!pendingPayouts[employeeID]) {
            pendingPayouts[employeeID] = {
                employeeID,
                businessID,
                totalAmount: 0,
                tips: [],
            };
        }
        pendingPayouts[employeeID].totalAmount += amount;
        pendingPayouts[employeeID].tips.push(tip);
    });

    return Object.values(pendingPayouts);
};


export const getPendingPayoutsByEmployeeID = async (businessID, employeeID) => {
    const tipsRef = collection(db, "tips");
    const pendingQuery = query(
        tipsRef,
        where("payoutStatus", "==", "PENDING"),
        where("businessID", "==", businessID),
        where("employeeID", "==", employeeID)
    );

    const querySnapshot = await getDocs(pendingQuery);

    let pendingPayout = {
        businessID,
        employeeID,
        totalAmount: 0,
        tips: [],
    };

    querySnapshot.forEach((doc) => {
        const tip = {id: doc.id, ...doc.data()};
        pendingPayout.totalAmount += tip.amount;
        pendingPayout.tips.push(tip);
    });

    return pendingPayout.tips.length > 0 ? pendingPayout : null;
};

/**
 * Fetch all completed payouts (from the "payouts" collection).
 */
export const getCompletedPayouts = async () => {
    const payoutsRef = collection(db, "payouts");
    const querySnapshot = await getDocs(payoutsRef);
    let completedPayouts = [];

    for (const payoutDoc of querySnapshot.docs) {
        const payout = {id: payoutDoc.id, ...payoutDoc.data()};
        const tipDocs = await Promise.all(payout.tipIDs.map((tipID) => getDoc(doc(db, "tips", tipID))));
        payout.tips = tipDocs.map((tipDoc) => ({id: tipDoc.id, ...tipDoc.data()}));
        completedPayouts.push(payout);
    }

    return completedPayouts;
};

/**
 * Fetch one newest-first page of completed payout summaries.
 * Tip documents are intentionally not hydrated here; the payout details page
 * fetches them only when an admin opens a specific payout.
 *
 * @param {Object} options
 * @param {import("firebase/firestore").DocumentSnapshot|null} options.cursor
 * @param {number} options.pageSize
 * @returns {Promise<{payouts: Array, nextCursor: Object|null, hasMore: boolean}>}
 */
export const getCompletedPayoutsPage = async ({cursor = null, pageSize = 10} = {}) => {
    const safePageSize = Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 10;
    const payoutsRef = collection(db, "payouts");
    const completedQuery = cursor
        ? query(
            payoutsRef,
            orderBy("createdAt", "desc"),
            startAfter(cursor),
            limit(safePageSize + 1)
        )
        : query(
            payoutsRef,
            orderBy("createdAt", "desc"),
            limit(safePageSize + 1)
        );

    const querySnapshot = await getDocs(completedQuery);
    const hasMore = querySnapshot.docs.length > safePageSize;
    const pageDocs = querySnapshot.docs.slice(0, safePageSize);
    const payouts = pageDocs.map((payoutDoc) => ({
        id: payoutDoc.id,
        ...payoutDoc.data(),
    }));

    return {
        payouts,
        hasMore,
        nextCursor: hasMore && pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    };
};

/**
 * Fetch completed payouts for a specific business.
 * @param {string} businessID - The business ID to filter payouts by.
 */
export const getCompletedPayoutsByBusinessID = async (businessID) => {
    const payoutsRef = collection(db, "payouts");
    const filteredQuery = query(payoutsRef, where("businessID", "==", businessID));

    const querySnapshot = await getDocs(filteredQuery);
    let completedPayouts = [];

    for (const payoutDoc of querySnapshot.docs) {
        const payout = {id: payoutDoc.id, ...payoutDoc.data()};

        const tipDocs = await Promise.all(
            payout.tipIDs.map((tipID) => getDoc(doc(db, "tips", tipID)))
        );

        payout.tips = tipDocs.map((tipDoc) => ({id: tipDoc.id, ...tipDoc.data()}));
        completedPayouts.push(payout);
    }

    return completedPayouts;
};
/**
 * Fetch details of a single payout.
 */
export const getPayoutDetails = async (payoutID) => {
    const payoutDoc = await getDoc(doc(db, "payouts", payoutID));
    if (!payoutDoc.exists()) return null;

    const payoutData = payoutDoc.data();
    const tipDocs = await Promise.all(payoutData.tipIDs.map((tipID) => getDoc(doc(db, "tips", tipID))));
    payoutData.tips = tipDocs.map((tipDoc) => ({id: tipDoc.id, ...tipDoc.data()}));

    return payoutData;
};
/**
 * Mark a payout as completed
 * @param {string} tipID - Firestore document ID of the tip
 */
export const markPayoutAsCompleted = async (tipID) => {
    try {
        const tipRef = doc(db, "tips", tipID);
        await updateDoc(tipRef, {
            paymentStatus: "PAID",
            paidAt: new Date(), // Add timestamp when it was paid
        });
        console.log(`✅ Payout marked as completed for tip ID: ${tipID}`);
    } catch (error) {
        console.error("Error marking payout as completed:", error);
    }
};


/**
 * Get total tip amounts (all-time, pending, and paid)
 * @returns {Promise<Object>} - { totalTips, totalPaid, totalPending }
 */
export const getAdminDashboardStats = async () => {
    try {
        const tipsRef = collection(db, "tips");

        // Fetch all tips
        const allTipsSnap = await getDocs(tipsRef);
        let totalTips = 0, totalPaid = 0, totalPending = 0;

        allTipsSnap.forEach((doc) => {
            const data = doc.data();
            if (data.amount) {
                totalTips += data.amount;
                if (data.payoutStatus === "PAID_OUT") {
                    totalPaid += data.amount;
                } else {
                    totalPending += data.amount;
                }
            }
        });

        return {totalTips, totalPaid, totalPending};
    } catch (error) {
        console.error("Error fetching admin dashboard stats:", error);
        return {totalTips: 0, totalPaid: 0, totalPending: 0};
    }
};

// 🏢 **BUSINESS MANAGEMENT** ==========================


/**
 * Fetch business details from Firestore
 * @param {string} businessID - The business's Firestore ID
 * @returns {Promise<Object|null>} - Business data or null if not found
 */
export const getBusinessData = async (businessID) => {
    try {
        const businessRef = doc(db, "businesses", businessID);
        const businessSnap = await getDoc(businessRef);
        return businessSnap.exists() ? businessSnap.data() : null;
    } catch (error) {
        console.error("Error fetching business data:", error);
        return null;
    }
};

/**
 * Fetch public business data from /publicBusinesses/{businessID}
 */
export const getPublicBusinessData = async (businessID) => {
    try {
        const businessRef = doc(db, "publicBusinesses", businessID);
        const businessSnap = await getDoc(businessRef);

        if (!businessSnap.exists()) {
            console.warn(`❌ Public business not found: ${businessID}`);
            return null;
        }

        return {id: businessSnap.id, ...businessSnap.data()};
    } catch (error) {
        console.error("❌ Error fetching public business data:", error);
        return null;
    }
};

/**
 * Fetch a public employee by tipID under /publicBusinesses/{businessID}/publicEmployees
 */
export const getPublicEmployeeByTipID = async (businessID, tipID) => {
    try {
        const employeesRef = collection(db, "publicBusinesses", businessID, "publicEmployees");
        const q = query(employeesRef, where("tipID", "==", tipID));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            console.warn(`❌ No public employee found for tipID ${tipID} in business ${businessID}`);
            return null;
        }

        return {id: snapshot.docs[0].id, ...snapshot.docs[0].data()};
    } catch (error) {
        console.error("❌ Error fetching public employee by tipID:", error);
        return null;
    }
};


/**
 * Fetch employee details from Firestore
 * @param {string} businessID - The business's Firestore ID
 * @param {string} employeeID - The employee's Firestore ID
 * @returns {Promise<Object|null>} - Employee data or null if not found
 */
export const getEmployeeData = async (businessID, employeeID) => {
    try {
        const employeeRef = doc(db, "businesses", businessID, "employees", employeeID);
        const employeeSnap = await getDoc(employeeRef);
        return employeeSnap.exists() ? employeeSnap.data() : null;
    } catch (error) {
        console.error("Error fetching employee data:", error);
        return null;
    }
};

/**
 * Fetch all businesses.
 */
export const getBusinesses = async () => {
    const querySnapshot = await getDocs(collection(db, "businesses"));
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
};

/**
 * Get a single business by ID.
 * @param {string} businessID
 */
export const getBusinessById = async (businessID) => {
    const docRef = doc(db, "businesses", businessID);
    const docSnap = await getDoc(docRef);
    console.log("Fetching data");
    return docSnap.exists() ? {id: docSnap.id, ...docSnap.data()} : null;
};


// 👨‍🍳 **EMPLOYEE MANAGEMENT** ==========================

/**
 * Fetch all employees for a specific business.
 * @param {string} businessID
 */
export const getEmployeeByBusiness = async (businessID) => {
    const querySnapshot = await getDocs(collection(db, `businesses/${businessID}/employees`));
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
};


// 💰 **TIP MANAGEMENT** ==========================

/**
 * Fetch all tips for a business.
 * @param {string} businessID
 */
// export const getTipsByBusiness = async (businessID) => {
//     const q = query(collection(db, "tips"), where("businessID", "==", businessID));
//     const querySnapshot = await getDocs(q);
//     return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
// };

export const getTipsByBusiness = async (businessID) => {
    const q = query(
        collection(db, "tips"),
        where("businessID", "==", businessID),
        orderBy("createdAt", "desc") // 👈 make sure 'createdAt' is indexed
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
};

/**
 * Fetch all tips for a specific employee.
 * @param {string} employeeID
 */
// export const getTipsByEmployee = async (employeeID) => {
//     const q = query(collection(db, "tips"), where("employeeID", "==", employeeID));
//     const querySnapshot = await getDocs(q);
//     return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
// };

export const getTipsByEmployee = async (businessID, employeeID) => {
    const q = query(
        collection(db, "tips"),
        where("businessID", "==", businessID),
        where("employeeID", "==", employeeID),
        orderBy("createdAt", "desc") // Sort by timestamp, newest first
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
};


// 💸 **PAYOUT MANAGEMENT** ==========================

/**
 * Fetch all payouts for a specific business.
 * @param {string} businessID
 */
export const getPayoutsByBusiness = async (businessID) => {
    const q = query(collection(db, "payouts"), where("businessID", "==", businessID));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
};

/**
 * Fetch all payouts for a specific employee.
 * @param {string} employeeID
 */
export const getPayoutsByEmployee = async (businessID, employeeID) => {
    const q = query(
        collection(db, "payouts"),
        where("businessID", "==", businessID),
        where("employeeID", "==", employeeID),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
};

export const getCompletedPayoutsByEmployee = async (businessID, employeeID) => {
    const q = query(
        collection(db, "payouts"),
        where("businessID", "==", businessID),
        where("employeeID", "==", employeeID),
    );
    const querySnapshot = await getDocs(q);
    const payouts = [];

    for (const payoutDoc of querySnapshot.docs) {
        const payout = {id: payoutDoc.id, ...payoutDoc.data()};

        if (Array.isArray(payout.tipIDs)) {
            const tipDocs = await Promise.all(
                payout.tipIDs.map((tipID) => getDoc(doc(db, "tips", tipID)))
            );
            payout.tips = tipDocs.map(tipDoc => ({
                id: tipDoc.id,
                ...tipDoc.data()
            }));
        } else {
            payout.tips = []; // fallback to avoid crash
        }

        payouts.push(payout);
    }

    return payouts;
};

/**
 * Update payout status (e.g., "pending" → "completed").
 * @param {string} payoutID
 * @param {Object} updatedData
 */
export const updatePayout = async (payoutID, updatedData) => {
    const docRef = doc(db, "payouts", payoutID);
    await updateDoc(docRef, updatedData);
};
